import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';

const CandidateLogin: React.FC = () => {
  const navigate = useNavigate();
  const [emailOrMobile, setEmailOrMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Demo candidate login - store in localStorage
    localStorage.setItem('candidateToken', 'demo-token');
    localStorage.setItem('candidateEmail', emailOrMobile || 'candidate@example.com');
    
    // Simulate login delay
    setTimeout(() => {
      setIsLoading(false);
      navigate('/candidate/dashboard');
    }, 500);
  };

  return (
    <>
      <Helmet>
        <title>Candidate Login - Club Corners</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-md w-full bg-white py-5 sm:py-6 px-5 sm:px-6 shadow-lg rounded-lg">
          <h2 className="text-xl sm:text-2xl font-bold text-blue-600 mb-3 text-left">
            Club Corners
          </h2>
          <div className="w-full h-0.5 bg-blue-600 mb-4"></div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 text-left">
            Candidate Login
          </h2>
          <p className="text-xs sm:text-sm text-gray-600 mb-5 text-left leading-tight">
            Sign in with the personal email address or mobile number you used during your Recruiting Process. Click on "Forgot Password?" to generate a new password to access your Onboarding Dashboard.
          </p>
          
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="emailOrMobile" className="block text-xs font-medium text-gray-700 mb-1.5">
                Email Address or mobile number
              </label>
              <input
                id="emailOrMobile"
                name="emailOrMobile"
                type="text"
                autoComplete="username"
                required
                className="input w-full text-sm py-2.5"
                placeholder="Enter your email or mobile number"
                value={emailOrMobile}
                onChange={e => setEmailOrMobile(e.target.value)}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="input w-full pr-10 text-sm py-2.5"
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  className="mr-1.5 w-3 h-3"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                />
                <label htmlFor="rememberMe" className="text-xs text-gray-700 select-none">
                  Remember Me
                </label>
              </div>
              <Link
                to="/forgot-password"
                className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
              >
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full py-2.5 text-sm mt-4"
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          
          {/* Demo credentials hint */}
          <div className="mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-blue-800 font-medium mb-1">Demo Credentials:</p>
              <p className="text-xs text-blue-700">
                Email/Mobile: <span className="font-mono">candidate@example.com</span>
              </p>
              <p className="text-xs text-blue-700">
                Password: <span className="font-mono">demo123</span>
              </p>
              <p className="text-xs text-blue-600 mt-2 italic">
                (Any email/password will work for demo)
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CandidateLogin;

